import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Pencil,
  Plus,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';
import clsx from 'clsx';
import { ApiError } from '@/lib/api';
import { apiClient } from '@/lib/api';
import {
  DeleteCaptchaField,
  useDeleteCaptcha,
} from '@/components/ui/delete-captcha';
import {
  createKnowledgeEntryApi,
  getKnowledgeEntryApi,
  listKnowledgeCategoriesApi,
  listKnowledgeColumnsApi,
  listKnowledgeEntriesApi,
  softDeleteKnowledgeEntryApi,
  updateKnowledgeEntryApi,
} from '@/lib/knowledge-api';
import type {
  CourseListEnvelope,
  CreateKnowledgeRequest,
  Difficulty,
  KnowledgeEntry,
  QuestionType,
} from '@/lib/types';
import {
  buildKnowledgeListQuery,
  KNOWLEDGE_DEFAULT_PAGE_SIZE,
  KNOWLEDGE_PAGE_SIZE_OPTIONS,
  resolveVisibleColumns,
  toggleColumn as toggleColumnHelper,
} from '@/lib/knowledge-filters';
import { Card } from '@/components/ui/card';
import { Modal } from '@/components/ui/modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';

// Issue #179：知识库「条目」工作区。课程筛选 + 高级检索 + 列隐藏（后端白名单）
// + 分页（默认 20，可 20/50/100，后端上限 100）+ 条目 CRUD（软删除）+ 详情。
// 所有读写走真实 /admin/knowledge* 接口，禁止任何前端假状态。

const PRIMARY_BTN =
  'inline-flex items-center gap-2 rounded-md bg-brand-500 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-brand-600 disabled:opacity-60';
const SECONDARY_BTN =
  'inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const DANGER_BTN =
  'inline-flex items-center gap-2 rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-rose-700 disabled:opacity-60';
const INPUT =
  'w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';

const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  SINGLE_CHOICE: '单选',
  MULTIPLE_CHOICE: '多选',
  TRUE_FALSE: '判断',
  FILL_BLANK: '填空',
  ESSAY: '问答',
};
const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  EASY: '简单',
  MEDIUM: '中等',
  HARD: '困难',
};
const QUESTION_TYPES = Object.keys(QUESTION_TYPE_LABEL) as QuestionType[];
const DIFFICULTIES = Object.keys(DIFFICULTY_LABEL) as Difficulty[];

/** 列中文名（仅用于展示，列本身只能来自后端白名单）。 */
const COLUMN_LABEL: Record<string, string> = {
  question: '题干',
  type: '题型',
  difficulty: '难度',
  answer: '答案',
  explanation: '解析',
  options: '选项',
  categoryId: '分类 ID',
  categoryName: '分类',
  courseId: '课程 ID',
  courseName: '课程',
  keywords: '关键词',
  tags: '标签',
  isActive: '状态',
  sortOrder: '排序',
  createdAt: '创建时间',
  updatedAt: '更新时间',
};

const COLUMN_STORAGE_KEY = 'jzcollege.knowledge.visibleColumns.v1';

function readStoredColumns(): string[] | null {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c) => typeof c === 'string') : null;
  } catch {
    return null;
  }
}

function fmtTime(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function errMessage(err: unknown): string {
  if (err instanceof ApiError) return err.status > 0 ? `${err.message}（HTTP ${err.status}）` : err.message;
  return err instanceof Error ? err.message : '操作失败，请稍后重试。';
}

type EntryModal =
  | { kind: 'create' }
  | { kind: 'edit'; id: string }
  | { kind: 'detail'; id: string }
  | { kind: 'delete'; entry: KnowledgeEntry }
  | null;

export function KnowledgeEntriesWorkspace() {
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<EntryModal>(null);
  const [columnPanelOpen, setColumnPanelOpen] = useState(false);

  // 筛选与分页（受控）。
  const [pendingKeyword, setPendingKeyword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [type, setType] = useState<QuestionType | ''>('');
  const [difficulty, setDifficulty] = useState<Difficulty | ''>('');
  const [courseId, setCourseId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'true' | 'false'>('all');
  const [sortBy, setSortBy] = useState<'sortOrder' | 'createdAt' | 'updatedAt'>('sortOrder');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(KNOWLEDGE_DEFAULT_PAGE_SIZE);

  // 列白名单（后端）。列隐藏状态浏览器持久化，但永远只请求白名单内的列。
  const columnsQuery = useQuery({
    queryKey: ['admin', 'knowledge', 'columns'],
    queryFn: listKnowledgeColumnsApi,
    staleTime: 5 * 60 * 1000,
  });
  const whitelist = columnsQuery.data?.columns ?? [];
  const [visibleColumns, setVisibleColumns] = useState<string[] | null>(null);

  // 白名单到达后初始化可见列：优先用持久化值，与白名单求交集；否则用默认集。
  useEffect(() => {
    if (whitelist.length === 0 || visibleColumns !== null) return;
    setVisibleColumns(resolveVisibleColumns(readStoredColumns(), whitelist));
  }, [whitelist, visibleColumns]);

  useEffect(() => {
    if (visibleColumns) {
      try {
        localStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(visibleColumns));
      } catch {
        /* 持久化失败不阻塞功能 */
      }
    }
  }, [visibleColumns]);

  const categoriesQuery = useQuery({
    queryKey: ['admin', 'knowledge', 'categories'],
    queryFn: listKnowledgeCategoriesApi,
    staleTime: 5 * 60 * 1000,
  });
  const coursesQuery = useQuery({
    // Issue #345：课程列表已后端分页（默认 20/页）。此处课程选择器需要全部课程，
    // 取满单页上限 pageSize=100 覆盖全组织课程（课程数远小于 100）。
    queryKey: ['admin', 'courses', 'selector'],
    queryFn: () =>
      apiClient.get<CourseListEnvelope>('/admin/courses', { pageSize: 100 }),
    staleTime: 60 * 1000,
  });
  const courses = coursesQuery.data?.items ?? [];
  const categories = categoriesQuery.data ?? [];

  const effectiveColumns = visibleColumns ?? [];
  const listQuery = useMemo(
    () =>
      buildKnowledgeListQuery({
        keyword,
        type,
        difficulty,
        courseId,
        categoryId,
        activeFilter,
        sortBy,
        sortOrder,
        page,
        pageSize,
        columns: effectiveColumns,
      }),
    [keyword, type, difficulty, courseId, categoryId, activeFilter, sortBy, sortOrder, page, pageSize, effectiveColumns],
  );

  const entriesQuery = useQuery({
    queryKey: ['admin', 'knowledge', 'list', listQuery],
    // 列白名单就绪后再查询，确保只请求白名单字段。
    enabled: visibleColumns !== null,
    queryFn: () => listKnowledgeEntriesApi(listQuery),
    placeholderData: keepPreviousData,
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'knowledge', 'list'] });
  const closeModal = () => setModal(null);

  function applyFilters(e: FormEvent) {
    e.preventDefault();
    setKeyword(pendingKeyword.trim());
    setPage(1);
  }
  function resetFilters() {
    setPendingKeyword('');
    setKeyword('');
    setType('');
    setDifficulty('');
    setCourseId('');
    setCategoryId('');
    setActiveFilter('all');
    setSortBy('sortOrder');
    setSortOrder('asc');
    setPage(1);
  }

  function toggleColumn(col: string) {
    setVisibleColumns((prev) => toggleColumnHelper(prev ?? [], col, whitelist));
  }

  const data = entriesQuery.data;
  const items = data?.items ?? [];
  // 后端回传的实际列（含 id），用于渲染表头；与本地可见列保持一致。
  const renderColumns = (data?.columns ?? ['id', ...effectiveColumns]).filter(
    (c) => c !== 'id',
  );
  const pagination = data?.pagination;

  return (
    <Card
      title="知识库条目"
      description="按课程 / 分类 / 关键词 / 题型 / 难度 / 状态检索知识库条目，支持列隐藏、分页与条目 CRUD。所有操作走真实后端接口。"
      action={
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={SECONDARY_BTN}
            onClick={() => setColumnPanelOpen((v) => !v)}
          >
            <SlidersHorizontal className="h-4 w-4" />
            列显示
          </button>
          <button
            type="button"
            className={PRIMARY_BTN}
            onClick={() => setModal({ kind: 'create' })}
          >
            <Plus className="h-4 w-4" />
            新增条目
          </button>
        </div>
      }
    >
      {/* 列隐藏 / 显示面板：选项仅来自后端白名单。 */}
      {columnPanelOpen ? (
        <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
          {columnsQuery.isError ? (
            <p className="text-xs text-rose-600">列白名单加载失败，无法配置列显示。</p>
          ) : (
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {whitelist.map((col) => (
                <label key={col} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={effectiveColumns.includes(col)}
                    onChange={() => toggleColumn(col)}
                  />
                  {COLUMN_LABEL[col] ?? col}
                </label>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {/* 筛选条件 */}
      <form
        className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-4"
        onSubmit={applyFilters}
        data-testid="knowledge-filter"
      >
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">关键词</span>
          <input
            className={INPUT}
            value={pendingKeyword}
            onChange={(e) => setPendingKeyword(e.target.value)}
            placeholder="题干 / 答案 / 解析 / 关键词 / 标签"
          />
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">课程</span>
          <select
            className={INPUT}
            value={courseId}
            onChange={(e) => {
              setCourseId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">全部课程</option>
            {courses.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">分类</span>
          <select
            className={INPUT}
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(1);
            }}
          >
            <option value="">全部分类</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">题型</span>
          <select
            className={INPUT}
            value={type}
            onChange={(e) => {
              setType(e.target.value as QuestionType | '');
              setPage(1);
            }}
          >
            <option value="">全部题型</option>
            {QUESTION_TYPES.map((t) => (
              <option key={t} value={t}>
                {QUESTION_TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">难度</span>
          <select
            className={INPUT}
            value={difficulty}
            onChange={(e) => {
              setDifficulty(e.target.value as Difficulty | '');
              setPage(1);
            }}
          >
            <option value="">全部难度</option>
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {DIFFICULTY_LABEL[d]}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">状态</span>
          <select
            className={INPUT}
            value={activeFilter}
            onChange={(e) => {
              setActiveFilter(e.target.value as 'all' | 'true' | 'false');
              setPage(1);
            }}
          >
            <option value="all">全部状态</option>
            <option value="true">启用</option>
            <option value="false">停用</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">排序字段</span>
          <select
            className={INPUT}
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as 'sortOrder' | 'createdAt' | 'updatedAt');
              setPage(1);
            }}
          >
            <option value="sortOrder">排序值</option>
            <option value="createdAt">创建时间</option>
            <option value="updatedAt">更新时间</option>
          </select>
        </label>
        <label className="text-xs text-slate-600">
          <span className="mb-1 block">排序方向</span>
          <select
            className={INPUT}
            value={sortOrder}
            onChange={(e) => {
              setSortOrder(e.target.value as 'asc' | 'desc');
              setPage(1);
            }}
          >
            <option value="asc">升序</option>
            <option value="desc">降序</option>
          </select>
        </label>
        <div className="flex items-end gap-2">
          <button type="submit" className={PRIMARY_BTN}>
            查询
          </button>
          <button type="button" className={SECONDARY_BTN} onClick={resetFilters}>
            重置
          </button>
        </div>
      </form>

      {entriesQuery.isLoading || visibleColumns === null ? (
        <LoadingState label="正在加载知识库条目…" />
      ) : entriesQuery.isError ? (
        <ErrorState
          title="知识库条目加载失败"
          description={errMessage(entriesQuery.error)}
          retry={() => void entriesQuery.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState title="暂无知识库条目" description="调整筛选条件，或点击「新增条目」创建。" />
      ) : (
        <div className="overflow-x-auto" data-testid="knowledge-table">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="text-xs text-slate-500">
              <tr>
                {renderColumns.map((col) => (
                  <th key={col} className="px-3 py-2 text-left font-medium">
                    {COLUMN_LABEL[col] ?? col}
                  </th>
                ))}
                <th className="px-3 py-2 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {items.map((row) => (
                <tr key={row.id} className="align-top hover:bg-slate-50">
                  {renderColumns.map((col) => (
                    <td key={col} className="px-3 py-2">
                      <Cell entry={row} col={col} />
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                        title="详情"
                        onClick={() => setModal({ kind: 'detail', id: row.id })}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-600"
                        title="编辑"
                        onClick={() => setModal({ kind: 'edit', id: row.id })}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className="rounded p-1 text-slate-500 hover:bg-rose-50 hover:text-rose-600"
                        title="软删除"
                        onClick={() => setModal({ kind: 'delete', entry: row })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 分页 */}
      {pagination ? (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <span>
              共 {pagination.total} 条 · 第 {pagination.page}/{pagination.totalPages} 页
            </span>
            <select
              className="rounded-md border border-slate-300 px-1.5 py-1"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              data-testid="knowledge-page-size"
            >
              {KNOWLEDGE_PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} 条/页
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 disabled:opacity-50"
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" /> 上一页
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 disabled:opacity-50"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页 <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ) : null}

      {modal?.kind === 'create' ? (
        <EntryFormModal
          key="kb-create"
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
      {modal?.kind === 'edit' ? (
        <EntryFormModal
          key={`kb-edit-${modal.id}`}
          entryId={modal.id}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
      {modal?.kind === 'detail' ? (
        <EntryDetailModal key={`kb-detail-${modal.id}`} entryId={modal.id} onClose={closeModal} />
      ) : null}
      {modal?.kind === 'delete' ? (
        <DeleteEntryModal
          key={`kb-delete-${modal.entry.id}`}
          entry={modal.entry}
          onClose={closeModal}
          onDone={refresh}
        />
      ) : null}
    </Card>
  );
}

function Cell({ entry, col }: { entry: KnowledgeEntry; col: string }) {
  switch (col) {
    case 'type':
      return <span>{entry.type ? QUESTION_TYPE_LABEL[entry.type] ?? entry.type : '—'}</span>;
    case 'difficulty':
      return <span>{entry.difficulty ? DIFFICULTY_LABEL[entry.difficulty] ?? entry.difficulty : '—'}</span>;
    case 'isActive':
      return (
        <span
          className={clsx(
            'rounded-full px-2 py-0.5 text-[10px] font-medium',
            entry.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600',
          )}
        >
          {entry.isActive ? '启用' : '停用'}
        </span>
      );
    case 'question':
    case 'answer':
    case 'explanation':
      return (
        <span className="block max-w-[28rem] truncate" title={entry[col] ?? ''}>
          {entry[col] || '—'}
        </span>
      );
    case 'options':
      return <span>{entry.options && entry.options.length > 0 ? `${entry.options.length} 项` : '—'}</span>;
    case 'createdAt':
    case 'updatedAt':
      return <span className="text-slate-500">{fmtTime(entry[col])}</span>;
    case 'courseName':
      return <span>{entry.courseName || '—'}</span>;
    case 'categoryName':
      return <span>{entry.categoryName || '—'}</span>;
    case 'sortOrder':
      return <span>{entry.sortOrder ?? '—'}</span>;
    default: {
      const v = (entry as unknown as Record<string, unknown>)[col];
      return <span>{v == null || v === '' ? '—' : String(v)}</span>;
    }
  }
}

// ─── 新增 / 编辑条目 ─────────────────────────────────────────────────────
function EntryFormModal({
  entryId,
  onClose,
  onDone,
}: {
  entryId?: string;
  onClose: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const editing = !!entryId;
  const detailQuery = useQuery({
    queryKey: ['admin', 'knowledge', 'detail', entryId],
    enabled: editing,
    queryFn: () => getKnowledgeEntryApi(entryId!),
  });
  const coursesQuery = useQuery({
    // Issue #345：课程选择器需全部课程；后端分页默认 20，这里取满 pageSize=100 覆盖全组织。
    queryKey: ['admin', 'courses', 'selector'],
    queryFn: () =>
      apiClient.get<CourseListEnvelope>('/admin/courses', { pageSize: 100 }),
    staleTime: 60 * 1000,
  });
  const categoriesQuery = useQuery({
    queryKey: ['admin', 'knowledge', 'categories'],
    queryFn: listKnowledgeCategoriesApi,
    staleTime: 5 * 60 * 1000,
  });

  const [question, setQuestion] = useState('');
  const [type, setType] = useState<QuestionType>('SINGLE_CHOICE');
  const [difficulty, setDifficulty] = useState<Difficulty>('MEDIUM');
  const [answer, setAnswer] = useState('');
  const [explanation, setExplanation] = useState('');
  const [courseId, setCourseId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [keywords, setKeywords] = useState('');
  const [tags, setTags] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // 编辑：详情到达后回填一次（弹窗已按 key 重挂载，无跨实体串档风险）。
  useEffect(() => {
    if (editing && detailQuery.data && !hydrated) {
      const d = detailQuery.data;
      setQuestion(d.question ?? '');
      setType((d.type as QuestionType) ?? 'SINGLE_CHOICE');
      setDifficulty((d.difficulty as Difficulty) ?? 'MEDIUM');
      setAnswer(d.answer ?? '');
      setExplanation(d.explanation ?? '');
      setCourseId(d.courseId ?? '');
      setCategoryId(d.categoryId ?? '');
      setKeywords(d.keywords ?? '');
      setTags(d.tags ?? '');
      setIsActive(d.isActive ?? true);
      setHydrated(true);
    }
  }, [editing, detailQuery.data, hydrated]);

  const mutation = useMutation({
    mutationFn: () => {
      const body: CreateKnowledgeRequest = {
        question: question.trim(),
        type,
        difficulty,
        answer: answer.trim(),
        explanation: explanation.trim() || undefined,
        courseId: courseId || undefined,
        categoryId: categoryId || undefined,
        keywords: keywords.trim() || undefined,
        tags: tags.trim() || undefined,
        isActive,
      };
      if (editing) {
        return updateKnowledgeEntryApi(entryId!, body);
      }
      return createKnowledgeEntryApi(body);
    },
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (err) => setError(errMessage(err)),
  });

  const loading = editing && detailQuery.isLoading;
  const disabled = mutation.isPending || !question.trim() || !answer.trim();
  const courses = coursesQuery.data?.items ?? [];
  const categories = categoriesQuery.data ?? [];

  return (
    <Modal
      wide
      title={editing ? '编辑知识库条目' : '新增知识库条目'}
      description={editing ? `条目 ID：${entryId}` : '创建一条知识库条目，所有写操作走真实后端接口。'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY_BTN} onClick={onClose}>
            取消
          </button>
          <button type="submit" form="kb-entry-form" className={PRIMARY_BTN} disabled={disabled}>
            {mutation.isPending ? '保存中…' : '保存'}
          </button>
        </>
      }
    >
      {loading ? (
        <LoadingState label="正在加载条目…" />
      ) : editing && detailQuery.isError ? (
        <ErrorState title="条目加载失败" description={errMessage(detailQuery.error)} />
      ) : (
        <form
          id="kb-entry-form"
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          <label className="block text-xs text-slate-600">
            <span className="mb-1 block">题干 *</span>
            <textarea
              className={INPUT}
              rows={3}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              maxLength={4096}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block">题型 *</span>
              <select className={INPUT} value={type} onChange={(e) => setType(e.target.value as QuestionType)}>
                {QUESTION_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {QUESTION_TYPE_LABEL[t]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block">难度 *</span>
              <select className={INPUT} value={difficulty} onChange={(e) => setDifficulty(e.target.value as Difficulty)}>
                {DIFFICULTIES.map((d) => (
                  <option key={d} value={d}>
                    {DIFFICULTY_LABEL[d]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="block text-xs text-slate-600">
            <span className="mb-1 block">答案 *</span>
            <textarea
              className={INPUT}
              rows={2}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              maxLength={20000}
            />
          </label>
          <label className="block text-xs text-slate-600">
            <span className="mb-1 block">解析</span>
            <textarea
              className={INPUT}
              rows={2}
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              maxLength={4096}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block">课程</span>
              <select className={INPUT} value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="">（不归属课程）</option>
                {courses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block">分类</span>
              <select className={INPUT} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                <option value="">（不归属分类）</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block">关键词（逗号分隔）</span>
              <input className={INPUT} value={keywords} onChange={(e) => setKeywords(e.target.value)} maxLength={256} />
            </label>
            <label className="block text-xs text-slate-600">
              <span className="mb-1 block">标签（逗号分隔）</span>
              <input className={INPUT} value={tags} onChange={(e) => setTags(e.target.value)} maxLength={256} />
            </label>
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            启用该条目
          </label>
          {error ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
              {error}
            </div>
          ) : null}
        </form>
      )}
    </Modal>
  );
}

// ─── 详情查看（长答案完整可读） ──────────────────────────────────────────
function EntryDetailModal({ entryId, onClose }: { entryId: string; onClose: () => void }) {
  const detailQuery = useQuery({
    queryKey: ['admin', 'knowledge', 'detail', entryId],
    queryFn: () => getKnowledgeEntryApi(entryId),
  });
  const d = detailQuery.data;
  return (
    <Modal
      wide
      title="知识库条目详情"
      description={`条目 ID：${entryId}`}
      onClose={onClose}
      footer={
        <button type="button" className={SECONDARY_BTN} onClick={onClose}>
          关闭
        </button>
      }
    >
      {detailQuery.isLoading ? (
        <LoadingState label="正在加载条目…" />
      ) : detailQuery.isError ? (
        <ErrorState title="条目加载失败" description={errMessage(detailQuery.error)} />
      ) : d ? (
        <div className="space-y-3 text-sm">
          <DetailField label="题干" value={d.question} />
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            <span>题型：{d.type ? QUESTION_TYPE_LABEL[d.type] ?? d.type : '—'}</span>
            <span>难度：{d.difficulty ? DIFFICULTY_LABEL[d.difficulty] ?? d.difficulty : '—'}</span>
            <span>课程：{d.courseName || '—'}</span>
            <span>分类：{d.categoryName || '—'}</span>
            <span>状态：{d.isActive ? '启用' : '停用'}</span>
          </div>
          {d.options && d.options.length > 0 ? (
            <div>
              <div className="mb-1 text-xs font-medium text-slate-600">选项</div>
              <ul className="space-y-1">
                {d.options.map((o) => (
                  <li key={o.key} className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                    <span className="font-medium">{o.key}.</span> {o.text}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <DetailField label="答案" value={d.answer} />
          <DetailField label="解析" value={d.explanation} />
          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
            <span>关键词：{d.keywords || '—'}</span>
            <span>标签：{d.tags || '—'}</span>
            <span>创建：{fmtTime(d.createdAt)}</span>
            <span>更新：{fmtTime(d.updatedAt)}</span>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}

function DetailField({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-slate-600">{label}</div>
      <div className="whitespace-pre-wrap break-words rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">
        {value || '—'}
      </div>
    </div>
  );
}

// ─── 软删除确认 ──────────────────────────────────────────────────────────
function DeleteEntryModal({
  entry,
  onClose,
  onDone,
}: {
  entry: KnowledgeEntry;
  onClose: () => void;
  onDone: () => Promise<unknown> | void;
}) {
  const [error, setError] = useState<string | null>(null);
  // Issue #235：删除前需通过后端签发的 6 位图形验证码。
  const captcha = useDeleteCaptcha('knowledge:delete');
  const [captchaCode, setCaptchaCode] = useState('');
  const mutation = useMutation({
    mutationFn: () =>
      softDeleteKnowledgeEntryApi(entry.id, {
        captchaId: captcha.captchaId ?? '',
        captchaCode,
      }),
    onSuccess: async () => {
      await onDone();
      onClose();
    },
    onError: (err) => {
      setError(errMessage(err));
      if (err instanceof ApiError && err.status === 400) {
        setCaptchaCode('');
        void captcha.refresh();
      }
    },
  });
  const canDelete =
    !mutation.isPending && !!captcha.captchaId && captchaCode.trim().length === 6;
  return (
    <Modal
      title="软删除知识库条目"
      description="软删除后条目从题库隐藏，但保留历史可由后端恢复（不物理删除）。"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={SECONDARY_BTN} onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className={DANGER_BTN}
            disabled={!canDelete}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending ? '删除中…' : '确认软删除'}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">
        确认软删除条目「{(entry.question ?? '').slice(0, 40) || entry.id}」？
      </p>
      <DeleteCaptchaField
        svg={captcha.svg}
        loading={captcha.loading}
        error={captcha.error}
        value={captchaCode}
        onChange={setCaptchaCode}
        onRefresh={captcha.refresh}
      />
      {error ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700">
          {error}
        </div>
      ) : null}
    </Modal>
  );
}
